import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaSave, FaEdit, FaClock, FaCopy } from "react-icons/fa";
import Dialog from "../../Admin/FormDialog/Dialog";
import { getUserInfo } from "../../../services/authService-sighup";
import { OUTAGE_TAB } from '../../Admin/constants/constants';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { convertAssetLocalToFormZone, findReferenceTimeZoneCode } from "../timezoneUtils";
import { applyCopiedIsoIds, copyComments, stripCopyMarkers } from "../isoIdUtils";
import { deriveOutageStatus } from "../outageStatusUtils";
import { OutageAssetScope, filterAssetsByScope, scopeForForm } from "../outageAssetScope";


// Mirrors the 13 codes in dbo.WORKFLOW_TYPE; keep in sync when codes are added there.
const WORKFLOW_TYPE_OPTIONS = [
    "Charge Limitation",
    "Curtailment Variance Event",
    "Derate",
    "Derate/Outage",
    "Embedded",
    "General",
    "ISO Notification",
    "IT",
    "Operating Instructions",
    "Outage",
    "Shift Turnover",
    "Testing",
    "unknown",
];

// Retired from the picker but still rendered while the loaded record already carries it.
// "Derate/Outage" is on ~11k prod outages, so dropping the option outright would blank
// the required Workflow field on every one of them. "Derate" and "Outage" are separate,
// still-active codes and stay selectable. "Shift Turnover" is likewise retired from the
// picker but kept in the list so legacy outages that use it still display and can be
// re-saved without blanking the required Workflow field.
const HIDDEN_WORKFLOW_TYPES = new Set(["Derate/Outage", "Shift Turnover"]);


interface Asset {
    asset_id: number;
    asset_name: string;
    retirement_date: string;
    group_status_code: string;
    time_zone_code?: string | null;
    resources?: Resource[];
}
interface Resource {
    resource_id: number;
    iso_resource_name: string;
}
interface Comment {
    logEntry: string;
    versifyLogid: string;
    versifyUpdateDate: string;
    updateUser: string;
    // Set only on details carried over by Copy; see copyComments. Never sent to the service.
    copiedIsoId?: string;
}



interface OutageFormProps {
    onCreateOutage: (participant: any) => Promise<void>;
    onSaveOutage: (participant: any) => Promise<void>;
    selectedOutage: any | null;
    assets: Asset[];
    assetScope: OutageAssetScope | null;
    assetScopeError: string | null;
    onClearSelection: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onModeChange: (mode: 'create' | 'edit' | 'view' | null) => void;
}

const OutageForm: React.FC<OutageFormProps> = ({
    onCreateOutage,
    onSaveOutage,
    selectedOutage,
    assets,
    assetScope,
    assetScopeError,
    onClearSelection,
    onDirtyChange,
    onModeChange,
}) => {
    
// const [reference_id, setRefrenceId] = useState('');
const [reference_id, setRefrenceId] = useState<number | "">(""); 
const [reference_type_code, setReferenceTypeCode] = useState('');
const [outage_status_code, setOutageStatusCode] = useState('');
const [workflow_type_code, setWorkflowTypeCode] = useState('');
const [start_datetime, setStartDateTime] = useState<Date | null>(null);
const [end_datetime, setEndDateTime] = useState<Date | null>(null);
const [startDate, setStartDate] = useState<Date | null>(null);
const [startTime, setStartTime] = useState<Date | null>(null);
const [endDate, setEndDate] = useState<Date | null>(null);
const [endTime, setEndTime] = useState<Date | null>(null);
const [comments, setComments] = useState<Comment[]>([]);
const [isEditMode, setIsEditMode] = useState(false);
const [isViewMode, setIsViewMode] = useState(false);
const formScope = scopeForForm(assetScope, isViewMode && !isEditMode);
const [activeField, setActiveField] = useState<string | null>(null);
const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({});
const [copiedFromOutageId, setCopiedFromOutageId] = useState<number | null>(null);
// The asset-local calendar date the end-of-day default was derived from. Non-null means
// the end value is still that auto-fill rather than a time the user typed, so re-picking
// the date can re-derive it against the current asset. Cleared on any manual time edit.
const [endAutoFillLocalDate, setEndAutoFillLocalDate] = useState<Date | null>(null);
// On create, Status follows the start/end dates until the user picks one by hand.
const [statusManuallySet, setStatusManuallySet] = useState(false);
const [formState, setFormState] = useState({
    reference_id: '',
    reference_type_code: '',
    outage_status_code: '', 
    workflow_type_code: '',
    start_datetime: '',
    end_datetime: '',
    comments: []
  });
  
const [errors, setErrors] = useState({
    reference_id: false,         
    outage_status_code: false,   
    workflow_type_code: false,   
    start_datetime: false,   
    end_datetime: false,   
    iso_id: false,
  });
  
  const validateFields = () => {
    const hasIsoId = comments.some((comment) => String(comment.versifyLogid ?? "").trim());
    const newErrors = {
      reference_id: !String(reference_id ?? '').trim(),
      outage_status_code: !String(outage_status_code ?? '').trim(),
      workflow_type_code: !String(workflow_type_code ?? '').trim(),
      start_datetime: !start_datetime,
      end_datetime: !end_datetime,
    iso_id: !isEditMode && !hasIsoId,
    };
  
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };
  
    const [user, setUser] = useState<{ fullName: string; email: string } | null>(null);

    useEffect(() => {
        if (selectedOutage) {
          // Convert string to Date object
          const start = selectedOutage.start_datetime ? new Date(selectedOutage.start_datetime) : null;
          const end = selectedOutage.end_datetime ? new Date(selectedOutage.end_datetime) : null;
      
          // Update pickers
          setStartDate(start);
          setStartTime(start);
          setEndDate(end);
          setEndTime(end);
      
          // Update combined datetime values
          setStartDateTime(start);
          setEndDateTime(end);
      
          // Also update the rest of the form
          setFormState({
            reference_id: selectedOutage.reference_id,
            reference_type_code: selectedOutage.reference_type_code,
            outage_status_code: selectedOutage.outage_status_code,
            workflow_type_code: selectedOutage.workflow_type_code,
            start_datetime: selectedOutage.start_datetime,
            end_datetime: selectedOutage.end_datetime,
            comments: selectedOutage.comments,
          });
        }
      }, [selectedOutage]);
      
  

  const [dialog, setDialog] = useState<{
      open: boolean;
      title: string;
      message: string;
      confirmButtonText: string;
      cancelButtonText?: string;
      onConfirm: () => void;
      onCancel?: () => void;
    }>({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });

    const closeDialog = () => {
      setDialog({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
      });
    };
  
    useEffect(() => {
      const fetchUserInfo = () => {
        const userInfo = getUserInfo();
        if (userInfo) {
          setUser({
            fullName: userInfo.fullName || "Unknown User",
            email: userInfo.email || "",
          });
        }
      };
      fetchUserInfo();
    }, []);

    useEffect(() => {
        if (selectedOutage) {
            // A loaded record's end time is whatever was saved, not an auto-fill.
            setCopiedFromOutageId(null);
            setEndAutoFillLocalDate(null);
        }

        if (selectedOutage && !isEditMode) {
            setReferenceTypeCode(selectedOutage.reference_type_code || '');
            setRefrenceId(selectedOutage.reference_id || '');
            setOutageStatusCode(selectedOutage.outage_status_code || '');
            setWorkflowTypeCode(selectedOutage.workflow_type_code || '');
            setStartDateTime(selectedOutage.start_datetime || '');
            setEndDateTime(selectedOutage.end_datetime || '');
            setComments(selectedOutage.comments || '');
          setIsViewMode(true);
          onDirtyChange(false);
        } else if (selectedOutage && isEditMode) {
            setReferenceTypeCode(selectedOutage.reference_type_code || '');   
            setRefrenceId(selectedOutage.reference_id || '');                
            setOutageStatusCode(selectedOutage.outage_status_code || '');    
            setWorkflowTypeCode(selectedOutage.workflow_type_code || '');    
            setStartDateTime(selectedOutage.start_datetime || '');    
            setEndDateTime(selectedOutage.end_datetime || '');    
            setComments(selectedOutage.comments || '');    
          setIsEditMode(false);
          setIsViewMode(true);
        }
      }, [selectedOutage]);
  
    const handleSubmit = () => {
        const isValid = validateFields();
        if (!isValid) {
        setDialog({
            open: true,
            title: OUTAGE_TAB.VALIDATION.MISSING_INFO_TITLE,
            message: OUTAGE_TAB.VALIDATION.MISSING_INFO_MESSAGE,
            confirmButtonText: "Close",
            onConfirm: closeDialog,
        });
        return;
        }
        // The form may have sat open since the dates were picked, so re-derive against
        // the current time rather than trusting the status shown when they were entered.
        const statusToSubmit = !isEditMode && !statusManuallySet
            ? deriveOutageStatus(start_datetime, end_datetime) || outage_status_code
            : outage_status_code;
        if (statusToSubmit !== outage_status_code) setOutageStatusCode(statusToSubmit);
        const outageData = {
            reference_id: reference_id,                     
            reference_type_code: reference_type_code,        
            outage_status_code: statusToSubmit,        
            workflow_type_code: workflow_type_code,         
            start_datetime: start_datetime,         
            end_datetime: end_datetime,         
            comments: stripCopyMarkers(comments),
        };
        console.log("Submitting outage_status_code:", start_datetime);
        console.log("Submitting outage_status_code:", end_datetime);

        

        if (isEditMode) {
        setDialog({
            open: true,
        title: OUTAGE_TAB.DIALOGS.CONFIRM_EDIT_TITLE,
            message: OUTAGE_TAB.DIALOGS.CONFIRM_EDIT_MESSAGE,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: async () => {
            closeDialog();
            await onSaveOutage(outageData);
            resetForm();
            },
            onCancel: closeDialog
        });
        } else {
        setDialog({
            open: true,
            title: OUTAGE_TAB.DIALOGS.CONFIRM_CREATE_TITLE,
            message: OUTAGE_TAB.DIALOGS.CONFIRM_CREATE_MESSAGE,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: async () => {
            closeDialog();
            await onCreateOutage(outageData);
            resetForm();
            },
            onCancel: closeDialog
        });
        }
    };
  
    const resetForm = () => {
        setReferenceTypeCode('');        // <-- new
        setRefrenceId('');               // <-- new
        setOutageStatusCode('');         // <-- new
        setWorkflowTypeCode('');         // <-- new
        setStartDateTime(null);
        setEndDateTime(null);
        setStartDate(null);
        setStartTime(null);
        setEndDate(null);
        setEndTime(null);
        setComments([]);
        setDetails({
            editIndex: null,
            details: '',
            versifyLogid: '',
        });
        setIsEditMode(false);
        setIsViewMode(false);
        onDirtyChange(false);
        onModeChange(null);
        setModifiedFields({});
        setCopiedFromOutageId(null);
        setEndAutoFillLocalDate(null);
        setStatusManuallySet(false);
    };


    const handleEdit = () => {
        setIsViewMode(false);
        setIsEditMode(true);
        onDirtyChange(true); 
        onModeChange('edit');
        setModifiedFields({});
        console.log("Edit clicked"); 

    };

    const resetToSelectedOutage = () => {
        if (selectedOutage) {
            setReferenceTypeCode(selectedOutage.reference_type_code || '');   
            setRefrenceId(selectedOutage.reference_id || '');                
            setOutageStatusCode(selectedOutage.outage_status_code || '');    
            setWorkflowTypeCode(selectedOutage.workflow_type_code || '');    
            setStartDateTime(selectedOutage.start_datetime || '');    
            setEndDateTime(selectedOutage.end_datetime || '');    
            setComments(selectedOutage.comments || '');    
            setDetails({
                editIndex: null,
                details: '',
                versifyLogid: '',
            });
            setModifiedFields({});
            setIsEditMode(false);
            setIsViewMode(true);
            onDirtyChange(false);
            onModeChange("view");
            setCopiedFromOutageId(null);
            setEndAutoFillLocalDate(null);
            setStatusManuallySet(false);
        }
    };
    
    const handleCancelClick = () => {
        setDialog({
        open: true,
        title: "Cancel Editing?",
        message: "Are you sure you want to discard changes?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
            closeDialog();
            resetToSelectedOutage();
            setModifiedFields({});
        },
        onCancel: closeDialog,
        });
    };

    const handleNew = () => {
        setDialog({
        open: true,
        title: OUTAGE_TAB.DIALOGS.CONFIRM_NEW_TITLE,
        message: OUTAGE_TAB.DIALOGS.CONFIRM_NEW_MESSAGE,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
            closeDialog();
            resetForm();
            // onModeChange('create');
            onDirtyChange(false);
            setIsEditMode(false);
            onClearSelection();
        },
        onCancel: closeDialog,
        });
    };

    // Duplicates the selected outage into an unsaved new entry: everything carries
    // over except Asset/Resource, which is the field the user is here to change.
    const handleCopy = () => {
        if (!selectedOutage) return;

        const sourceOutageId = selectedOutage.outage_id;
        const start = selectedOutage.start_datetime ? new Date(selectedOutage.start_datetime) : null;
        const end = selectedOutage.end_datetime ? new Date(selectedOutage.end_datetime) : null;

        // Drop the parent selection first: while it is still set the form counts as
        // viewing that record, and the populate effect would overwrite the copy.
        onClearSelection();

        setRefrenceId("");
        setReferenceTypeCode("");

        setOutageStatusCode(selectedOutage.outage_status_code || '');
        setWorkflowTypeCode(selectedOutage.workflow_type_code || '');

        // Every detail carries over. Numeric ISO-IDs are held unique by the service, so
        // they stay only while the copy's workflow is Embedded and are blanked otherwise.
        // Returns a new array, so edits here never touch the source outage's list.
        setComments(copyComments(selectedOutage.comments, selectedOutage.workflow_type_code));

        // Re-seed the datetimes as Date objects. The view-mode effect leaves these as
        // raw strings, which the create path (DateTime.fromJSDate) cannot read.
        setStartDate(start);
        setStartTime(start);
        setStartDateTime(start);
        setEndDate(end);
        setEndTime(end);
        setEndDateTime(end);

        setDetails({ editIndex: null, details: '', versifyLogid: '' });
        setErrors({
            reference_id: false,
            outage_status_code: false,
            workflow_type_code: false,
            start_datetime: false,
            end_datetime: false,
            iso_id: false,
        });
        setModifiedFields({});
        setEndAutoFillLocalDate(null);
        // The copy is a new outage, so its status is re-derived from the copied dates.
        setStatusManuallySet(false);

        setIsViewMode(false);
        setIsEditMode(false);
        setCopiedFromOutageId(sourceOutageId);
        onModeChange('create');
        onDirtyChange(true);
    };



    // Resources have no timezone of their own, so a Resource reference resolves to the
    // timezone of the asset it belongs to.
    const assetTimeZoneCode = findReferenceTimeZoneCode(assets, reference_id, reference_type_code);

    // Every field on this form is Eastern, because that is the clock the Boston users
    // read. Picking an end date therefore means "end of day at the asset", stored as its
    // Eastern equivalent: 23:59 on a CAISO asset lands at 02:59 the following day.
    const applyEndOfDayDefault = (localDate: Date, timeZoneCode?: string | null) => {
        const assetLocalEndOfDay = new Date(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            23,
            59,
            0,
            0
        );

        // Falls back to a plain 23:59 Eastern when the asset timezone is missing or
        // can't be mapped, which is the behaviour this form has always had.
        const inFormZone =
            convertAssetLocalToFormZone(assetLocalEndOfDay, timeZoneCode) ?? assetLocalEndOfDay;

        setEndDate(inFormZone);
        setEndTime(inFormZone);
        setEndDateTime(inFormZone);
        setEndAutoFillLocalDate(localDate);
    };

    // Create only: keep Status in step with the window (future start -> Approved,
    // running now -> Active, already ended -> Completed) until the user overrides it.
    useEffect(() => {
        if (isEditMode || isViewMode || statusManuallySet) return;
        const derived = deriveOutageStatus(start_datetime, end_datetime);
        if (derived) setOutageStatusCode(derived);
    }, [start_datetime, end_datetime, isEditMode, isViewMode, statusManuallySet]);

    const handleDateChange = (
        field: "start_datetime" | "end_datetime",
        date: Date | null,
        existingTime: Date | null
    ) => {
        if (!date) return;

        if (field === "end_datetime") {
            // Auto-derive the asset-local end-of-day preset only for the first end-date
            // choice, when no time is on screen yet. Once a date+time value is present,
            // changing the date keeps that displayed time rather than re-converting, so
            // the day no longer jumps forward on every subsequent pick.
            if (!existingTime) {
                applyEndOfDayDefault(date, assetTimeZoneCode);
                return;
            }

            // A value is already present: keep the shown time and just move the day.
            const combined = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                existingTime.getHours(),
                existingTime.getMinutes(),
                0,
                0
            );

            setEndDate(date);
            setEndTime(existingTime);
            setEndDateTime(combined);
            // The end value is now a user-adjusted time, not the asset end-of-day default.
            setEndAutoFillLocalDate(null);
            return;
        }

        // Start defaults to midnight and is never timezone-shifted.
        const finalTime = existingTime ?? new Date(0, 0, 0, 0, 0, 0, 0);
        const combined = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            finalTime.getHours(),
            finalTime.getMinutes(),
            finalTime.getSeconds(),
            finalTime.getMilliseconds()
        );

        setStartDate(date);
        setStartTime(finalTime);
        setStartDateTime(combined);
    };

    const handleTimeChange = (field: "start_datetime" | "end_datetime", time: Date | null, existingDate: Date | null) => {
        if (!time) return;

        // The clock button is also the initial preset. When no date has been chosen
        // yet, use the clock's date so the preset appears and remains editable.
        const selectedDate = existingDate ?? time;
        const updatedDateTime = new Date(selectedDate);
        updatedDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);

        if (field === "end_datetime") {
        setEndDate(selectedDate);
        setEndTime(time);
        setEndDateTime(updatedDateTime);
        // Typed by hand, so stop treating the end value as the asset end-of-day default.
        setEndAutoFillLocalDate(null);
        } else {
        setStartDate(selectedDate);
        setStartTime(time);
        setStartDateTime(updatedDateTime);
        }
    };



    const isLoading = !assets || assets.length === 0;
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [details, setDetails] = useState<{
        editIndex: number | null;
        details: string;
        versifyLogid: string;
    }>({
        editIndex: null,
        details: "",
        versifyLogid: "",
    });
    const formatversifyDateTime = (date: Date): string => {
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
    
        return `${month}/${day}/${year} ${hours}:${minutes}`;
    };

    const handleEditComment = (index: number): void => {
        const comment = comments[index];
        setDetails({
            editIndex: index,
            details: comment.logEntry,
            versifyLogid: comment.versifyLogid || "", 
        });
    };
    const handleDeleteComment = (index: number): void => {
        setDialog({
            open: true,
            title: "Delete Comment?",
            message: "Are you sure you want to delete this comment?",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: () => {
            closeDialog();
            const updatedComments = comments.filter((_, i) => i !== index);
            setComments(updatedComments);
            },
            onCancel: closeDialog,
        });
    };

    const handleAddComment = (): void => {
        if (details.details.trim() !== '') {
            // Length rule applies only to a value that was actually entered; an empty
            // ISO-ID is allowed here (e.g. a status-update comment on an existing outage).
            // "Required on create" is enforced separately in validateFields.
            const isoId = details.versifyLogid.trim();
            if (isoId.length > 0 && isoId.length < 6) {
                alert("ISO-ID must be at least 6 characters.");
                return;
            }
            const newComment: Comment = {
                logEntry: details.details,
                versifyUpdateDate: formatversifyDateTime(new Date()),
                versifyLogid: details.versifyLogid.trim(),
                updateUser: user ? `${user.fullName || user.email}` : "Unknown User"
                // updateUser: "unknown",
            };

            setComments((prevComments) => [...prevComments, newComment]);
            if (newComment.versifyLogid) {
                setErrors((previousErrors) => ({ ...previousErrors, iso_id: false }));
            }

            setDetails({
                editIndex: null,
                details: '',
                versifyLogid: '',
            });
        } else {
            alert("Details cannot be empty!");
        }
    };
    const handleUpdateComment = (): void => {
        if (details.editIndex !== null && details.details.trim() !== '') {
            const isoId = details.versifyLogid.trim();
            if (isoId.length > 0 && isoId.length < 6) {
                alert("ISO-ID must be at least 6 characters.");
                return;
            }
            setComments((prevComments) =>
                prevComments.map((comment, index) =>
                    index === details.editIndex
                        ? {
                            ...comment,
                            logEntry: details.details,
                            versifyLogid: details.versifyLogid,
                            versifyUpdateDate: formatversifyDateTime(new Date()),
                            // An ISO-ID the user typed is theirs, so a later workflow change
                            // must not overwrite it; an untouched one stays tied to the copy.
                            copiedIsoId: details.versifyLogid === comment.versifyLogid
                                ? comment.copiedIsoId
                                : undefined,
                        }
                        : comment
                )
            );

            setDetails({
                editIndex: null,
                details: '',
                versifyLogid: '',
            });
        }
    };
    const handleChangeDetails = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // ISO-ID accepts letters and symbols as well as digits -- pure-letter values
        // like "economics" are used deliberately as reason codes. Uniqueness is
        // enforced server side, not by restricting what can be typed.
        const sanitizedValue = name === 'versifyLogid'
            ? value.replace(/\s/g, '').slice(0, 12)
            : value;

        setDetails((prevState) => ({
            ...prevState,
            [name]: sanitizedValue,
        }));
    };


    // const handleAssetResourceSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //     const selectedId = Number(e.target.value);

    //     if (selectedId) {
    //         const selectedAsset = assets.find(asset => asset.asset_id === selectedId);
    //         const selectedResource = assets.flatMap(asset => asset.resources || []).find(res => res.resource_id === selectedId);

    //         if (selectedAsset) {
    //             // setRefrenceId(selectedAsset.asset_id);
    //             setReferenceTypeCode("Asset");
    //         } else if (selectedResource) {
    //             // setRefrenceId(selectedResource.resource_id);
    //             setReferenceTypeCode("Resource");
    //         }
    //     } else {
    //         setRefrenceId("");
    //         setReferenceTypeCode("");
    //     }
    // };
    const handleAssetResourceSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const [type, id] = e.target.value.split("-");
        const selectedId = Number(id);
      
        if (type && selectedId) {
          if (type === "asset") {
            const selectedAsset = assets.find(asset => asset.asset_id === selectedId);
            if (selectedAsset) {
              setReferenceTypeCode("Asset");
              setRefrenceId(selectedAsset.asset_id); 
            }
          } else if (type === "resource") {
            const selectedResource = assets
              .flatMap(asset => asset.resources || [])
              .find(res => res.resource_id === selectedId);
            if (selectedResource) {
              setReferenceTypeCode("Resource");
              setRefrenceId(selectedResource.resource_id);
            }
          }
        } else {
          setRefrenceId("");
          setReferenceTypeCode("");
        }
      };
      


    


// Asset of the outage being viewed/edited, so it stays selectable when outside the scope.
const editedOutageAssetId: number | undefined = !selectedOutage || reference_id === ""
    ? undefined
    : reference_type_code?.toLowerCase() === "resource"
        ? assets.find(a => a.resources?.some(r => r.resource_id === reference_id))?.asset_id
        : Number(reference_id);

return (
<div className="flex flex-col bg-white rounded-xl shadow-xl p-6 short:p-4 h-full overflow-auto">

{/* <div className="flex-grow flex flex-col lg:basis-1/3 bg-white rounded-xl shadow-xl p-6 overflow-auto h-full"> */}
    
    {dialog.open && (
    <Dialog
        title={dialog.title}
        message={dialog.message}
        confirmButtonText={dialog.confirmButtonText}
        cancelButtonText={dialog.cancelButtonText}
        onConfirm={dialog.onConfirm}
        onCancel={dialog.onCancel}
        onClose={closeDialog}
    />
    )}
    <div className="flex items-center justify-end gap-4 mb-4">
    {/* Sits inside the action row rather than above it: the right-hand panel is a
        fixed-height flex child, so a banner on its own line pushes the form past the
        panel and raises a scrollbar the layout is designed not to have. Kept to one
        truncated line (full text in the tooltip) so it can never grow the row. */}
    {copiedFromOutageId !== null && (
        <div
        title={`Copied from outage #${copiedFromOutageId}. Pick a new Asset/Resource, then click Create to save it as a new entry.`}
        className="mr-auto min-w-0 truncate rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
        Copied from <b>#{copiedFromOutageId}</b> &mdash; pick a new Asset/Resource, then Create.
        </div>
    )}
    {!isViewMode && !isEditMode && (
        <>
        <button
        onClick={handleSubmit}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
        <FaPlus className="w-4 h-4" />
        Create
        </button>

        <button
        onClick={handleNew}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
        Clear
        </button>
        </>
    )}

    {isViewMode && !isEditMode && (
        <>
        <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
            <FaPlus className="w-4 h-4" />
            New
        </button>

        <button
            onClick={handleCopy}
            title="Duplicate this outage as a new entry, keeping everything except Asset/Resource"
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
            <FaCopy className="w-4 h-4" />
            Copy
        </button>

        <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
            <FaEdit className="w-4 h-4" />
            Edit
        </button>
        </>
    )}

    {isEditMode && (
        <>
        <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
            <FaSave className="w-4 h-4" />
            Save
        </button>

        <button
            onClick={handleCancelClick}
            className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
        >
            
            Cancel
        </button>
        </>
    )}
    </div>

    {/* Form Rows */}

    <div className="shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5 short:mt-3">
        <div>
        <label className="block text-gray-600 mb-1">Asset/Resource<span className="text-red-500 text-lg align-top ml-1">*</span></label>
        {isLoading ? (
            <p>Loading assets...</p>
        ) : error ? (
            <p className="text-red-500">{error}</p>
        ) : !formScope && assetScopeError ? (
            <p className="text-red-500">{assetScopeError}</p>
        ) : !formScope ? (
            <select disabled className="w-full p-3 mt-[-4px] border rounded-lg shadow-inner text-sm bg-[#f9f9f9] text-gray-500 border-gray-300">
            <option value="">Loading assets…</option>
            </select>
        ) : (
            <select
            name="reference_id"
            value={
                reference_id && reference_type_code
                ? `${reference_type_code.toLowerCase()}-${reference_id}`
                : ""
            }
            onChange={(e) => {
                const [type, id] = e.target.value.split("-");
                handleAssetResourceSelection(e);
                onDirtyChange?.(true);
                setModifiedFields?.(prev => ({ ...prev, reference_id: true }));
                // The new asset may sit in another zone, so re-derive an end-of-day
                // auto-fill against it. A time typed by hand is left alone.
                if (endAutoFillLocalDate) {
                    applyEndOfDayDefault(
                        endAutoFillLocalDate,
                        findReferenceTimeZoneCode(
                            assets,
                            Number(id),
                            type === "resource" ? "Resource" : "Asset"
                        )
                    );
                }
            }}
            onFocus={() => setActiveField("reference_id")}
            disabled={isViewMode && !isEditMode}
            className={`w-full p-3 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                ${errors.reference_id && !reference_id
                ? "border-red-500 focus:ring-red-500"
                : isEditMode && (activeField === "reference_id" || modifiedFields?.reference_id)
                    ? "border-green-500 focus:ring-2 focus:ring-green-500"
                    : !isEditMode
                    ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                    : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
            >
            <option value="">Select Asset/Resource</option>
            {filterAssetsByScope(assets, formScope, editedOutageAssetId)
                .filter(asset => {
                const now = new Date();
                const isSpecialDate = asset.retirement_date === "1900-01-01T00:00:00";
                const isNullDate = !asset.retirement_date;
                const retirementDate = asset.retirement_date ? new Date(asset.retirement_date) : undefined;

                const isNotRetired =
                    isSpecialDate || isNullDate || (retirementDate !== undefined && retirementDate >= now);

                const isStatusAllowed = ["Active", "Onboarding"].includes(asset.group_status_code);

                return isNotRetired && isStatusAllowed;
                })
                .sort((a, b) => a.asset_name.localeCompare(b.asset_name))
                .map((asset) => (
                <React.Fragment key={`asset-${asset.asset_id}`}>
                    <option value={`asset-${asset.asset_id}`}>{asset.asset_name}</option>
                    {asset.resources && asset.resources.length > 1 &&
                    asset.resources
                        .filter(resource => resource.iso_resource_name)
                        .map(resource => (
                        <option
                            key={`resource-${resource.resource_id}`}
                            value={`resource-${resource.resource_id}`}
                        >
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{resource.iso_resource_name}
                        </option>
                        ))}
                </React.Fragment>
                ))}
            </select>
        )}
        </div>
        <div>
        <label className="block text-gray-600 mb-1 ">Resource Type Code</label>
        <input
            type="text"
            placeholder="Resource Type Code"
            value={reference_type_code || "Resource || Asset"}
            readOnly
            disabled
            className="w-full p-3 border rounded-lg shadow-inner text-sm transition-all duration-200 bg-[#f9f9f9] text-gray-800 border-gray-300"
        />
        </div>
    </div>


    <div className="shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5 short:mt-3">
        {/* Status Dropdown */}
        <div>
            <label className="block text-gray-600 mb-1">
            Status<span className="text-red-500 text-lg align-top ml-1">*</span>
            {!isEditMode && !isViewMode && !statusManuallySet && outage_status_code && (
                <span className="text-xs text-gray-400 ml-2">(set from start/end date)</span>
            )}
            </label>
            <select
            name="outage_status_code"
            value={outage_status_code}
            onChange={(e) => {
                console.log("Dropdown changed to:", e.target.value);
                setOutageStatusCode(e.target.value);
                setStatusManuallySet(true);
                onDirtyChange?.(true);
                setModifiedFields?.(prev => ({ ...prev, outage_status_code: true }));
            }}
            disabled={isViewMode && !isEditMode}
            className={`w-full p-3 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                ${errors.outage_status_code && !outage_status_code
                    ? "border-red-500 focus:ring-red-500"
                    : isEditMode && (activeField === "outage_status_code" || modifiedFields?.outage_status_code)
                        ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
            >
            <option value="" disabled>Select Status</option>
            <option value="Active">Active</option>
            <option value="Approved">Approved</option>
            <option value="Completed">Completed</option>
            <option value="Deleted">Deleted</option>
            </select>
        </div>

        {/* Workflow Dropdown */}
        <div>
            <label className="block text-gray-600 mb-1 ">
            Workflow<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>
            <select
            name="workflow_type_code"
            value={workflow_type_code}
            onChange={(e) => {
                const nextWorkflowTypeCode = e.target.value;
                setWorkflowTypeCode(nextWorkflowTypeCode);
                // Copied numeric ISO-IDs are only valid on an Embedded outage.
                setComments((prev) => applyCopiedIsoIds(prev, nextWorkflowTypeCode));
                onDirtyChange?.(true);
                setModifiedFields?.(prev => ({ ...prev, workflow_type_code: true }));
            }}
            disabled={isViewMode && !isEditMode}
            className={`w-full p-3 mt-[-4px] border rounded-lg shadow-inner text-sm transition-all duration-200
                ${errors.workflow_type_code && !workflow_type_code
                    ? "border-red-500 focus:ring-red-500"
                    : isEditMode && (activeField === "workflow_type_code" || modifiedFields?.workflow_type_code)
                        ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                }`}
            >
            <option value="" disabled>Select Workflow</option>
            {WORKFLOW_TYPE_OPTIONS.filter(
                option => !HIDDEN_WORKFLOW_TYPES.has(option) || option === workflow_type_code
            ).map(option => (
                <option key={option} value={option}>{option}</option>
            ))}
            </select>
        </div>
    </div>


    <div className="shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5 short:mt-3">
        {/* Start Date */}
        <div>
        <label className="block text-gray-600 mb-1">
            Start Date<span className="text-red-500 text-lg align-top ml-1">*</span>
        </label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center z-100">
            <div className="flex w-full gap-2">
                {/* Start Date Picker */}
                {/* <div className="w-1/2"> */}
                <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => handleDateChange("start_datetime", date, startTime)}
                disabled={isViewMode && !isEditMode}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className={`w-full p-3 border rounded-l-lg shadow-inner text-sm transition-all duration-200
                    ${errors.start_datetime && !startDate
                    ? "border-red-500 focus:ring-red-500"
                    : isEditMode && (activeField === "start_datetime" || modifiedFields?.start_datetime)
                        ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                />
                {/* End Time Picker */}
                <DatePicker
                selected={startTime}
                onChange={(time: Date | null) => handleTimeChange("start_datetime", time, startDate)}
                disabled={isViewMode && !isEditMode}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={1}
                timeCaption="Time"
                timeFormat="HH:mm"
                dateFormat={["HH:mm", "HHmm"]}
                placeholderText="Select time"
                className={`w-full p-3 border rounded-r-lg shadow-inner text-sm transition-all duration-200
                    ${errors.start_datetime && !startTime
                        ? "border-red-500 focus:ring-red-500"
                        : isEditMode && (activeField === "start_datetime" || modifiedFields?.start_datetime)
                            ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                />
                {/* Clock Button */}
                <button
                type="button"
                disabled={isViewMode && !isEditMode}
                className={`p-3 border rounded-lg transition duration-200 
                    ${
                    isViewMode && !isEditMode
                        ? "text-gray-300 cursor-not-allowed bg-gray-100"
                        : "text-gray-500 hover:text-blue-500"
                    }`}
                onClick={() => {
                    if (!(isViewMode && !isEditMode)) handleTimeChange("start_datetime", new Date(), startDate);
                }}
                >
                <FaClock />
                </button>
                {/* </div> */}
            </div>
            </div>
        </div>

        {/* End Date */}
        <div>
        <label className="block text-gray-600 mb-1">
            End Date<span className="text-red-500 text-lg align-top ml-1">*</span>
        </label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
            <div className="flex w-full gap-2">
                {/* End Date Picker */}
                <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => handleDateChange("end_datetime", date, endTime)}
                disabled={isViewMode && !isEditMode}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className={`w-full p-3 border rounded-l-lg shadow-inner text-sm transition-all duration-200
                    ${errors.end_datetime && !endDate
                    ? "border-red-500 focus:ring-red-500"
                    : isEditMode && (activeField === "end_datetime" || modifiedFields?.end_datetime)
                        ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                />
                {/* End Time Picker */}
                <DatePicker
                selected={endTime}
                onChange={(time: Date | null) => handleTimeChange("end_datetime", time, endDate)}
                disabled={isViewMode && !isEditMode}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={1}
                timeCaption="Time"
                timeFormat="HH:mm"
                dateFormat={["HH:mm", "HHmm"]}
                placeholderText="Select time"
                className={`w-full p-3 border rounded-r-lg shadow-inner text-sm transition-all duration-200
                    ${errors.end_datetime && !endTime
                        ? "border-red-500 focus:ring-red-500"
                        : isEditMode && (activeField === "end_datetime" || modifiedFields?.end_datetime)
                            ? "border-green-500 focus:ring-2 focus:ring-green-500"
                        : !isEditMode
                            ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                            : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                />
                {/* Clock Button */}
                <button
                type="button"
                disabled={isViewMode && !isEditMode}
                className={`p-3 border rounded-lg transition duration-200
                    ${
                    isViewMode && !isEditMode
                        ? "text-gray-300 cursor-not-allowed bg-gray-100"
                        : "text-gray-500 hover:text-blue-500"
                    }`}
                onClick={() => {
                    if (!(isViewMode && !isEditMode)) handleTimeChange("end_datetime", new Date(), endDate);
                }}
                >
                <FaClock />
                </button>
            </div>
            </div>
        </div>
    </div>


    <div className="flex flex-1 min-h-0 flex-col mt-5 short:mt-3">
        <div className="flex flex-1 min-h-0 flex-col">
            <label className="block text-gray-600 mb-1 shrink-0">Details</label>
            <div
            className="flex-1 min-h-[7rem] overflow-y-auto p-3 border rounded-lg shadow-inner bg-[#f9f9f9] text-gray-800 border-gray-300"
            ref={containerRef}
            >
            {comments && Array.isArray(comments) && comments.length > 0 ? (
                comments.map(
                (
                    comment: {
                    logEntry: string;
                    versifyUpdateDate: string;
                    updateUser: string;
                    versifyLogid: string;
                    copiedIsoId?: string;
                    },
                    index: number
                ) => (
                    <div key={index} className="border-b py-2 last:border-none">
                    <p className="text-sm text-gray-700 mb-1">
                        <b>{comment.updateUser}</b> -{" "}
                        {new Date(comment.versifyUpdateDate).toLocaleString("en-US", {
                        timeZone: "America/New_York",
                        hour12: false,
                        })}{" "}
                        {comment.versifyLogid && (
                        <>| ISO-ID-<b>{comment.versifyLogid}</b> | </>
                        )}
                        {!comment.versifyLogid && comment.copiedIsoId && (
                        <>
                            |{" "}
                            <span
                            className="text-amber-700"
                            title={`ISO-ID ${comment.copiedIsoId} belongs to the source outage. Edit this detail to enter the new outage's ISO-ID, or set Workflow to Embedded to keep it.`}
                            >
                            ISO-ID cleared (was {comment.copiedIsoId})
                            </span>{" "}
                            |{" "}
                        </>
                        )}
                        <span
                        className={`ml-2 font-bold ${
                            isViewMode && !isEditMode
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-blue-600 cursor-pointer"
                        }`}
                        onClick={() => {
                            if (!(isViewMode && !isEditMode)) handleEditComment(index);
                        }}
                        >
                        Edit
                        </span>
                        {" | "}
                        <span
                        className={`font-bold ${
                            isViewMode && !isEditMode
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-red-500 cursor-pointer"
                        }`}
                        onClick={() => {
                            if (!(isViewMode && !isEditMode)) handleDeleteComment(index);
                        }}
                        >
                        Delete
                        </span>
                    </p>
                    <p className="text-gray-800 text-sm whitespace-pre-wrap">
                        {comment.logEntry}
                    </p>
                    </div>
                )
                )
            ) : (
                <p className="text-gray-500 text-sm">No comments available...</p>
            )}
            </div>
        </div>


        <div className="shrink-0 mt-6 short:mt-3">
            <div className="flex items-center gap-3 mb-4 short:mb-2">
            <label className="text-gray-700 font-medium">
                ISO-ID:{!isEditMode && !isViewMode && (
                    <span className="text-red-500 text-lg align-top ml-1">*</span>
                )}
            </label>
            <input
                type="text"
                name="versifyLogid"
                value={details.versifyLogid}
                onChange={handleChangeDetails}
                disabled={isViewMode && !isEditMode}
                placeholder="Enter ISO-ID"
                maxLength={12}
                className={`w-52 p-2 border rounded-lg shadow-inner text-sm bg-white text-gray-800 focus:ring-2 ${
                    errors.iso_id ||
                    (details.versifyLogid.trim().length > 0 && details.versifyLogid.trim().length < 6)
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                }`}
            />
            <span
                className={`text-xs ${
                    details.versifyLogid.trim().length > 0 && details.versifyLogid.trim().length < 6
                        ? "text-red-500"
                        : "text-gray-500"
                }`}
            >
                Must be at least 6 characters.
            </span>
            </div>


            <textarea
            name="details"
            rows={5}
            value={details.details}
            onChange={handleChangeDetails}
            disabled={isViewMode && !isEditMode}
            placeholder="Add your comments here..."
            className="w-full h-28 short:h-20 p-3 mb-3 short:mb-2 border rounded-lg shadow-inner text-sm bg-white text-gray-800 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />


            <button
            type="button"
            disabled={isViewMode && !isEditMode}
            className={`inline-flex items-center px-4 py-2 rounded-lg transition duration-200 text-sm
                ${
                isViewMode && !isEditMode
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
            onClick={
                !(isViewMode && !isEditMode)
                ? (details.editIndex !== null ? handleUpdateComment : handleAddComment)
                : undefined
            }
            >
            {details.editIndex !== null ? "Update Comment" : "Add Comment"}
            </button>

        </div>
    </div>


</div>
  );
};

export default OutageForm;